import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  type RequestState,
  createInitialRequestState,
} from "@/store/requests/requestState";

export const REQUEST_KEYS = {
  SPARXS_CATALOG_OVERVIEW: "sparxsCatalogOverview",
  CHANNEL_DETAIL: "channelDetail",
  EXECUTIVE_SUMMARY: "executiveSummary",
} as const;

/** Slice state: requestKey -> subKey -> RequestState. Data is unknown; consumers cast by key. */
export type RequestsState = Record<
  string,
  Record<string, RequestState<unknown>>
>;

const initialState: RequestsState = {};

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    sparxsCatalogOverviewSubscriptionRequested: () => {
      /* No state change; epic listens and starts stream. */
    },
    sparxsCatalogOverviewSubscriptionCancelled: () => {
      /* No state change; epic listens and tears down stream. */
    },
    channelDetailSubscriptionRequested: () => {
      /* No state change; epic listens and starts stream. */
    },
    channelDetailSubscriptionCancelled: () => {
      /* No state change; epic listens and tears down stream. */
    },
    executiveSummarySubscriptionRequested: () => {
      /* No state change; epic listens and starts stream. */
    },
    executiveSummarySubscriptionCancelled: () => {
      /* No state change; epic listens and tears down stream. */
    },
    requestStart: (
      state,
      action: PayloadAction<{ requestKey: string; subKey: string }>,
    ) => {
      const { requestKey, subKey } = action.payload;
      if (state[requestKey] == null) {
        state[requestKey] = {};
      }
      // Every requestStart begins a new FI/period context, so the previous payload
      // would be wrong data; lastFetchedAt stays for boundary and reconnect signals.
      const prev = state[requestKey][subKey];
      state[requestKey][subKey] = createInitialRequestState({
        status: "loading",
        lastFetchedAt: prev?.lastFetchedAt,
      });
    },
    requestSuccess: (
      state,
      action: PayloadAction<{
        requestKey: string;
        subKey: string;
        data: unknown;
      }>,
    ) => {
      const { requestKey, subKey, data } = action.payload;
      if (state[requestKey] == null) {
        state[requestKey] = {};
      }
      state[requestKey][subKey] = createInitialRequestState({
        status: "success",
        data,
        lastFetchedAt: Date.now(),
      });
    },
    requestError: (
      state,
      action: PayloadAction<{
        requestKey: string;
        subKey: string;
        error: string;
        httpStatus?: number;
      }>,
    ) => {
      const { requestKey, subKey, error, httpStatus } = action.payload;
      if (state[requestKey] == null) {
        state[requestKey] = {};
      }
      const prev = state[requestKey][subKey];
      state[requestKey][subKey] = createInitialRequestState({
        status: "error",
        error,
        ...(httpStatus !== undefined ? { httpStatus } : {}),
        data: prev?.data ?? null,
        lastFetchedAt: prev?.lastFetchedAt,
      });
    },
    requestReset: (
      state,
      action: PayloadAction<{ requestKey: string; subKey: string }>,
    ) => {
      const { requestKey, subKey } = action.payload;
      if (state[requestKey]?.[subKey] != null) {
        state[requestKey][subKey] = createInitialRequestState();
      }
    },
  },
});

export const {
  requestStart,
  requestSuccess,
  requestError,
  requestReset,
  sparxsCatalogOverviewSubscriptionRequested,
  sparxsCatalogOverviewSubscriptionCancelled,
  channelDetailSubscriptionRequested,
  channelDetailSubscriptionCancelled,
  executiveSummarySubscriptionRequested,
  executiveSummarySubscriptionCancelled,
} = requestsSlice.actions;
export default requestsSlice.reducer;
